/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../actions'
import Card from '../../common/Card'
import $t from '../../../i18n'
import Plan from './Plan/Plan'
import { Dropdown, Button } from 'antd'
import PlansFilterDialog from './PlansFilterDialog'
import {
  getPriceAsFloat,
  isIncludedInPlanCoverage,
  isPlanFavorite,
  sortPlansByRank,
  sortPlansHighToLow,
  sortPlansLowToHigh
} from '../../../utils/planUtils'
import { MAX_COMPARE_PLANS, MAX_SAVED_PLANS } from 'consts/planConsts'
import { reverse } from 'lodash'
import routes from 'consts/routes'
import Page from 'components/common/Page'
import ActionButton from 'components/common/ActionButton'
import useApplicationValidation from 'hooks/useApplicationValidation'

import './Plans.css'

const Plans = () => {
  const dispatch = useDispatch()
  useApplicationValidation()
  const tabs = {
    recommended: $t('enroll.plans.tabs.recommended'),
    all: $t('enroll.plans.tabs.all'),
    saved: $t('enroll.plans.tabs.saved')
  }

  const sort = {
    recommended: $t('enroll.plans.sort.bestMatch'),
    low: $t('enroll.plans.sort.lowToHigh'),
    high: $t('enroll.plans.sort.highToLow'),
    saved: $t('enroll.plans.sort.recentlySaved')
  }

  const RECOMMENDED_TAB_COUNT = 3

  const { plans, planRank, comparePlans, isPlansLoading, savedPlans } =
    useSelector(state => state.plan)
  const locale = useSelector(state => state.locale?.locale)

  const [selectedTab, setSelectedTab] = useState(tabs.all)
  const [displayPlans, setDisplayPlans] = useState(plans)
  const [filterPlansCount, setFilterPlansCount] = useState(plans?.length)
  const [sortBy, setSortBy] = useState({
    label: sort.recommended,
    key: 'recommended'
  })
  // only for mobile view
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const shouldShowFilter = selectedTab === tabs.all
  const isRecommendedTab = selectedTab === tabs.recommended

  // check if the plan is already in the compare or saved list and disabled accordingly
  const isCompareDisabled = plan =>
    comparePlans?.length >= MAX_COMPARE_PLANS && !isCompareChecked(plan)
  const isFavoriteDisabled = plan =>
    savedPlans?.length >= MAX_SAVED_PLANS && !isPlanFavorite(plan, savedPlans)

  const isCompareChecked = plan =>
    comparePlans?.find(
      comparePlan => comparePlan?.antidote_plan_id === plan?.antidote_plan_id
    )

  const sortOptions = [
    {
      key: 'recommended',
      label: sort.recommended
    },
    {
      key: 'low',
      label: sort.low
    },
    {
      key: 'high',
      label: sort.high
    },
    {
      key: 'saved',
      label: sort.saved,
      disabled: selectedTab !== tabs.saved
    }
  ]

  useEffect(() => {
    dispatch(Actions.createAction(Actions.PLANS_REQUESTED))
    setSelectedTab(planRank ? tabs.recommended : tabs.all)
  }, [locale])

  useEffect(() => {
    let displayPlans = plans

    if (selectedTab === tabs.saved) {
      displayPlans = savedPlans
    } else if (selectedTab === tabs.recommended) {
      displayPlans = plans?.slice(0, RECOMMENDED_TAB_COUNT)
    }

    setDisplayPlans(displayPlans)
    setFilterPlansCount(plans?.length)
    const sortBy =
      selectedTab === tabs.saved
        ? { label: sort.saved, key: 'saved' }
        : { label: sort.recommended, key: 'recommended' }
    setSortBy(sortBy)
    onSortChange(sortBy?.key, displayPlans)
  }, [selectedTab, plans])

  const onSortChange = (key, plans) => {
    let sortedPlans = plans
    const sortValue = sort[key]
    if (sortValue === sort.low) {
      sortedPlans = sortPlansLowToHigh(plans)
    } else if (sortValue === sort.high) {
      sortedPlans = sortPlansHighToLow(plans)
    } else if (sortValue === sort.saved) {
      // saved plans is sorted by recently saved, reverse the array
      sortedPlans = reverse([...savedPlans])
    } else {
      sortedPlans = sortPlansByRank(plans, planRank)
    }

    setSortBy({ label: sortValue, key: key })
    setDisplayPlans(sortedPlans)
  }

  const onFilterChange = selectedFilters => {
    const filterPlans = plans?.filter(plan => {
      const isPlanCategoryMatch = selectedFilters?.planCategory?.length
        ? selectedFilters?.planCategory?.includes(plan?.details?.color)
        : true
      const isCoverageCategoryMatch = selectedFilters?.coverageCategory?.length
        ? isIncludedInPlanCoverage(
            plan?.name,
            selectedFilters?.coverageCategory
          )
        : true
      return (
        plan.plan_rate_multiplied <= selectedFilters?.monthlyPremium &&
        getPriceAsFloat(plan.details?.Annual_Deductible) <=
          selectedFilters?.annualDeductible &&
        isPlanCategoryMatch &&
        isCoverageCategoryMatch
      )
    })

    setFilterPlansCount(filterPlans?.length)
    setDisplayPlans(filterPlans)
  }

  const getPlanCountByTab = tab => {
    if (tab === tabs.saved) {
      return savedPlans?.length
    } else if (tab === tabs.recommended) {
      return RECOMMENDED_TAB_COUNT
    }
    return filterPlansCount === plans?.length
      ? plans?.length
      : $t('enroll.plans.planFilterCount', {
          count: filterPlansCount,
          total: plans?.length
        })
  }

  const onComapreClick = () => {
    // navigate to plan compare page
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.ENROLL_COMPARE))
  }

  const onComparePlanRemoved = plan => {
    dispatch(Actions.createAction(Actions.PLAN_COMPARE_REMOVED, { plan }))
  }

  const onComparePlansClear = () => {
    dispatch(Actions.createAction(Actions.PLAN_COMPARE_CLEARED))
  }

  return (
    <Page
      title={$t('dialogs.comparePlans.title')}
      className='plans-page'
      loading={isPlansLoading}
    >
      <div className='plans-actions'>
        <div className='plans-tabs'>
          {Object.values(tabs)?.map(tab => (
            <ActionButton
              className='tab'
              isHighlight={selectedTab === tab}
              onClick={() => setSelectedTab(tab)}
            >
              {tab} ({getPlanCountByTab(tab)})
            </ActionButton>
          ))}
        </div>
        <div className='row'>
          {shouldShowFilter && (
            <ActionButton
              className='plans-filter-btn'
              onClick={() => setIsFilterOpen(true)}
              isHighlight={filterPlansCount !== plans?.length}
              icon='icon-sliders'
            >
              {$t('enroll.plans.filter.filter')}
            </ActionButton>
          )}
          <ActionButton
            className='plans-sort'
            isHighlight={!!sortBy?.key}
            icon='icon-calendar'
          >
            <Dropdown
              overlayClassName='plans-sort-dropdown'
              getPopupContainer={triggerNode => triggerNode.parentNode}
              menu={{
                items: sortOptions,
                onSelect: ({ key }) => onSortChange(key, displayPlans),
                selectable: true,
                selectedKeys: [sortBy?.key]
              }}
            >
              <div className='selected-sort'>
                {sortBy?.label} <i className='icon-arrow-thin-down' />
              </div>
            </Dropdown>
          </ActionButton>
        </div>
      </div>

      <div className='plans-container'>
        <div className='plans-list' data-with-filter={shouldShowFilter}>
          {/* <div className='plans-list-sort'>
            <div
              className='compare-plans'
              onClick={comparePlans?.length > 1 ? onComapreClick : () => {}}
              data-active={comparePlans?.length > 1}
            >
              <i className='compare-icon icon-bar-chart' />
              <span className='link'>
                {$t('enroll.plans.plan.compareSelected')}
              </span>
            </div>
          </div> */}

          {displayPlans?.map(plan => (
            <Card className='plan-card' key={plan?.antidote_plan_id}>
              <Plan
                plan={plan}
                displayRecommendedFlag={!isRecommendedTab}
                isCompareDisabled={isCompareDisabled(plan)}
                isFavoriteDisabled={isFavoriteDisabled(plan)}
                isCompareChecked={isCompareChecked(plan)}
                isFavorite={isPlanFavorite(plan, savedPlans)}
              />
            </Card>
          ))}
        </div>
      </div>
      <PlansFilterDialog
        isFilterOpen={isFilterOpen}
        onCancel={() => setIsFilterOpen(false)}
        plans={plans}
        onChange={onFilterChange}
        selectedTab={selectedTab}
        onApply={() => setIsFilterOpen(false)}
      />
      {!!comparePlans?.length && (
        <Card className='compare-plans-popup-list'>
          {comparePlans?.map(plan => (
            <div className='plan-compare-name semi-bold'>
              <span>{plan?.name?.split('(')?.[0]}</span>
              <i
                className='icon-ex'
                onClick={() => onComparePlanRemoved(plan)}
              />
            </div>
          ))}
          <div className='compare-plans-actions'>
            <Button
              className='compare-plans-btn'
              type='primary'
              onClick={onComapreClick}
            >
              {$t('enroll.plans.plan.compare')}
            </Button>
            <Button className='compare-plans-btn' onClick={onComparePlansClear}>
              {$t('enroll.plans.plan.clear')}
            </Button>
          </div>
        </Card>
      )}
    </Page>
  )
}

export default Plans
